import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect
} from "react";
import CaretSlimIcon from "views/components/icons/CaretSlimIcon";
import ScrollingOverflowText from "views/components/ScrollingOverflowText/ScrollingOverflowText";
import style from "./style.module.css";

interface SubmenuOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  isActive?: boolean;
}

interface ParentOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface ContextMenuSubmenuProps {
  parentOption: ParentOption;
  submenuOptions: SubmenuOption[];
  onOptionClick: (key: string) => void;
}

const ContextMenuSubmenu = ({
  parentOption,
  submenuOptions,
  onOptionClick
}: ContextMenuSubmenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const allDisabled =
    submenuOptions.length > 0 && submenuOptions.every(opt => opt.disabled);

  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (parentOption.disabled || allDisabled) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    openTimerRef.current = setTimeout(() => setIsOpen(true), 100);
  }, [parentOption.disabled, allDisabled]);

  const handleMouseLeave = useCallback(() => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const panel = panelRef.current;
    const rect = panel.getBoundingClientRect();
    let transformX = 0;
    let transformY = 0;

    if (rect.right > window.innerWidth) {
      // Flip to the left side of the parent
      transformX = -(rect.width + (panel.parentElement?.offsetWidth ?? 0));
    }

    if (rect.bottom > window.innerHeight) {
      transformY = window.innerHeight - rect.bottom;
    }

    if (transformX !== 0 || transformY !== 0) {
      panel.style.transform = `translate(${transformX}px, ${transformY}px)`;
    } else {
      panel.style.transform = "";
    }
  }, [isOpen]);

  return (
    <div
      className={style.submenuParent}
      data-disabled={allDisabled || parentOption.disabled}
      data-testid={`submenu-parent-${parentOption.key}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={style.icon}>{parentOption.icon}</div>
      {parentOption.label}
      <div className={style.chevron}>
        {/* @ts-expect-error CaretSlimIcon is untyped JS */}
        <CaretSlimIcon
          color={
            allDisabled || parentOption.disabled
              ? "var(--color-easil-gray)"
              : "var(--color-easil-black)"
          }
          size="12px"
          direction="right"
        />
      </div>
      {isOpen && (
        <div
          className={style.submenuPanel}
          ref={panelRef}
          data-testid={`submenu-panel-${parentOption.key}`}
        >
          {submenuOptions.map(option => (
            <div
              key={option.key}
              className={style.submenuOption}
              data-disabled={option.disabled}
              data-active={option.isActive || false}
              data-testid={`submenu-option-${option.key}`}
              onClick={() => !option.disabled && onOptionClick(option.key)}
            >
              <div className={style.icon}>{option.icon}</div>
              <ScrollingOverflowText className={style.optionLabel}>
                {option.label}
              </ScrollingOverflowText>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContextMenuSubmenu;
